import { getUserByID } from '@adornis/buchungsheld-db/api/users.js';
import { getProductByID } from '@adornis/buchungsheld-db/api/product.js';
import { paymentPluginDefaultCreatePayment, paymentPluginDefaultGetPaymentLink } from '../api/default.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer, saveEntityData } from '@adornis/bh-util/util.js';
import { getParticipationsByID } from '@adornis/buchungsheld-db/api/participation-helper.js';
import {
  getParticipationCurrentPaymentServer,
  getParticipationsByIDs,
} from '@adornis/buchungsheld-db/api/participation.js';
import { getUserProductsForWhichAPaymentExists } from '@adornis/buchungsheld-db/api/payment.js';
import type { PaymentPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { notifyBestaetigungWorkshopRegistrierung } from '@adornis/buchungsheld-db/notification/bestaetigung-workshop-registrierung.js';
import { Price } from '@adornis/payments/db/prices.js';
import { refundPayment } from '@adornis/payments/server/payment.js';
import { goTo } from '@adornis/router/client/open-href.js';

export const getProductPrice = async (productID: string, userID: string) => {
  const product = await getProductByID(productID)({
    prices: { ...selectionSet(() => Price, 2), _id: 1, unitAmount: 1 },
  });
  if (!product) throw new Error('could not find product ' + productID);
  return product?.prices?.[0] as Maybe<Price>;
};

export const defaultPaymentPlugin = {
  id: 'payment::default',
  type: 'payment',
  paymentMethods: ['card', 'paypal'],
  getProductPrice,
  createPayment: async (participationIDs: string[], paymentMethod) => {
    const user = getCurrentUserServer();
    if (!user) throw new Error('user not found');

    const participations = await getParticipationsByID(participationIDs)({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      isNoShow: 1,
      signoffDate: 1,
      registrationDate: 1,
      fulfilledParticipationRequirements: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const participationsEinzelberatung = await getParticipationsByID(participationIDs)({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      isNoShow: 1,
      signoffDate: 1,
      registrationDate: 1,
      fulfilledParticipationRequirements: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const userOwnedProductIDs = await getUserProductsForWhichAPaymentExists()();
    const newParticipations = [...participations, ...participationsEinzelberatung].filter(
      pa => !userOwnedProductIDs.includes(pa.productID as string),
    );
    if (newParticipations.length === 0) {
      throw new Error('no participations found to create a payment with');
    }

    const institutionID = newParticipations[0]?.product?.institutionID;
    if (!institutionID) throw new Error('something went wrong');

    const paymentID = await paymentPluginDefaultCreatePayment(institutionID, newParticipations, paymentMethod)();
    return [paymentID];
  },

  pay: async (paymentID, paymentMethod) => {
    const paymentLink = await paymentPluginDefaultGetPaymentLink(paymentID, paymentMethod)();
    if (!paymentLink) throw new Error('could not create payment Link');

    await goTo(paymentLink, { target: '_self' });
  },

  afterEnrollment: async participationID => {},

  cancleEnrollment: async (participationID: string) => {
    const payment = await getParticipationCurrentPaymentServer(participationID);
    if (!payment) return;
    switch (payment.status) {
      case 'paid':
        // @ts-expect-error TODO: make engine types narrowedEntity
        await refundPayment({ paymentInstance: payment, reason: 'requested_by_customer' });
        return;
      case 'created':
      case 'issued':
        payment.status = 'canceled';
        await saveEntityData(payment);
        return;
      case 'canceled':
        // do nothing; already canceled
        return;
      default:
        throw new Error('handling of paymentState=' + payment.status + ' is not implemented');
    }
  },

  sendInvoice: async (paymentID: string) => false,
  onPaymentUpdate: async (institutionID, paymentOld, paymentNew) => {
    if (!paymentOld) return;
    const statusChanged = paymentOld.status !== paymentNew.status;

    if (statusChanged && paymentNew.status === 'paid') {
      const participations = await getParticipationsByIDs(paymentNew.participationIDs as string[])({
        _id: 1,
        userID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
          username: 1,
          whatsappNotification: 1,
          whatsappPhone: 1,
          suspentions: { _id: 1, institutionID: 1 },
          receivesNewsletter: 1,
        },
        productID: 1,
        product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
        productPriceForUser: { unitAmount: 1, currency: 1 },
      });

      const p1 = participations[0];
      if (!p1) return;
      const user = await getUserByID(p1.userID as string)({
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      });
      if (!user) throw new Error('user not found');
      await notifyBestaetigungWorkshopRegistrierung(institutionID, user as User, participations);
    }
  },
  priceGroups: ['default'],
} satisfies PaymentPlugin<'card' | 'paypal', 'default'>;
